import { isTalentPoolRoute } from '../../virtco/components/talent-pool/layout/top-header/utils/is-talent-pool-route';
import { TALENT_POOL_HOME_PATH } from '../../virtco/components/talent-pool/routes/paths';
import { isPublicPath } from '../const/enums';
import { isReferralLandingPath } from './referral-code-handler';

/** Keep in sync with CAMPAIGN_LANDING_SOURCES in utm-tracking.tsx */
function isCampaignLandingPath(pathname: string): boolean {
  const normalized =
    pathname.length > 1 && pathname.endsWith('/')
      ? pathname.slice(0, -1)
      : pathname;
  return (
    normalized === '/linkedin' ||
    normalized === '/instagram' ||
    normalized === '/jobboard' ||
    normalized === '/utm' ||
    normalized.startsWith('/utm/')
  );
}

/** Guest (logged-out) home URLs under /home/* */
export const GUEST_CONSULTANT_HOME_PATH = '/home/consultant';
export const GUEST_CLIENT_HOME_PATH = '/home/client';
export const GUEST_HOME_PATH = '/home';

/** Logged-in home URLs */
export const CLIENT_HOME_PATH = '/client-home';
export const CONSULTANT_HOME_PATH = '/consultant-home';

/** Primary destination for authenticated header/sidebar logo clicks */
export const DASHBOARD_PATH = '/dashboard';

/**
 * Logo click target:
 * logged-in on Talent Pool → Talent Pool dashboard;
 * logged-in on VirtUp → VirtUp dashboard;
 * guests → guest home.
 */
export function getLogoDestinationPath(isLoggedIn: boolean, pathname = ''): string {
  if (!isLoggedIn) return GUEST_CLIENT_HOME_PATH;
  if (pathname && isTalentPoolRoute(pathname)) return TALENT_POOL_HOME_PATH;
  return DASHBOARD_PATH;
}

export const LOGGED_IN_HOME_PATHS = [
  CLIENT_HOME_PATH,
  CONSULTANT_HOME_PATH,
  '/career-home',
  '/marketing-home',
  '/it-home',
] as const;

export type LoggedInHomePath = (typeof LOGGED_IN_HOME_PATHS)[number];

export function isLoggedInHomePath(pathname: string): boolean {
  return (LOGGED_IN_HOME_PATHS as readonly string[]).includes(pathname);
}

/** Guest home: landing page defaults to the client audience */
export function getGuestHomePath(_usertype?: string | null): string {
  return GUEST_CLIENT_HOME_PATH;
}

/** Logged-in home follows the selected/authenticated role */
export function getLoggedInHomePath(usertype?: string | null): string {
  return usertype === 'Seller' || usertype === 'Consultant'
    ? CONSULTANT_HOME_PATH
    : CLIENT_HOME_PATH;
}

/** Pick guest or logged-in home URL based on auth state */
export function getHomePath(usertype?: string | null, isLoggedIn = false): string {
  return isLoggedIn ? getLoggedInHomePath(usertype) : getGuestHomePath(usertype);
}

const isBareHomePath = (path: string): boolean => {
  const normalized = path.replace(/\/$/, '') || '/';
  return normalized === '/home';
};

const GUEST_TO_LOGGED_IN_HOME_PATH: Record<string, string> = {
  [GUEST_CLIENT_HOME_PATH]: CLIENT_HOME_PATH,
  [GUEST_CONSULTANT_HOME_PATH]: CONSULTANT_HOME_PATH,
  '/home/marketing': CLIENT_HOME_PATH,
  '/home/career': CLIENT_HOME_PATH,
  '/home/itconsultant': CLIENT_HOME_PATH,
};

function resolveLoggedInGuestHomePath(
  pathname: string,
  userType?: string | null
): string | null {
  const normalized = pathname.replace(/\/$/, '') || '/';
  if (isBareHomePath(normalized)) {
    return getLoggedInHomePath(userType);
  }

  if (
    normalized === GUEST_CLIENT_HOME_PATH ||
    normalized === GUEST_CONSULTANT_HOME_PATH
  ) {
    return getLoggedInHomePath(userType);
  }

  return GUEST_TO_LOGGED_IN_HOME_PATH[normalized] ?? null;
}

/**
 * Resolve navigation target: bare `/home` becomes role-based home.
 * Pass `isLoggedIn: true` after login; omit or false for guest/logout flows.
 */
export function resolveHomeDestination(
  path: string | undefined | null,
  userType?: string | null,
  isLoggedIn = false
): string {
  const target = (path || '').trim();
  const pathnameOnly = target.split('?')[0] || '';
  const isReferralReturn = isReferralLandingPath(pathnameOnly);
  const isCampaignReturn = isCampaignLandingPath(pathnameOnly);
  const loggedInGuestHomePath = isLoggedIn
    ? resolveLoggedInGuestHomePath(pathnameOnly, userType)
    : null;

  if (loggedInGuestHomePath) {
    const queryString = target.includes('?') ? `?${target.split('?').slice(1).join('?')}` : '';
    return `${loggedInGuestHomePath}${queryString}`;
  }

  if (!target || isBareHomePath(pathnameOnly) || isReferralReturn || isCampaignReturn) {
    return getHomePath(userType, isLoggedIn);
  }

  const isExplicitPublicReturn =
    !!target &&
    !pathnameOnly.startsWith('/auth') &&
    isPublicPath(pathnameOnly) &&
    !isReferralReturn &&
    !isCampaignReturn;
  if (isExplicitPublicReturn) {
    return target;
  }
  return target;
}

/** After logout: land on unified guest home (avoids /home/client → /home redirect flash). */
export function getLogoutHomePath(_pathBeforeLogout?: string, _userType?: string | null): string {
  return GUEST_HOME_PATH;
}
